import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { isStringArray } from 'utils/isArray';
import Button from './Button';
import { ActionDrawer } from './Drawer';
import CheckboxList from './Drawer/CheckboxList';
import Input from './form/Inputs/Input';
import LabelledCheckbox from './form/Inputs/LabelledCheckbox';
import ChevronDownIcon from './icons/ChevronDown';
// Icons
import CrossIcon from './icons/Cross';
import FilterIcon from './icons/Filter';
import SearchIcon from './icons/Search';
import TakeSelect from './list/TakeSelect';

const ColumnTitle = styled.div`
  margin-bottom: 1rem;
  cursor: pointer;
  color: var(--gray-700);
  font-weight: 500;
  display: flex;
  justify-content: space-between;
  stroke: var(--gray-500);
  & > div {
    transform: rotate(0deg);
    transition: 0.2s;
  }
  .open {
    transform: rotate(180deg);
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  max-width: 100%;
  row-gap: 0.5rem;
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  row-gap: 0.5rem;

  & > * {
    margin-right: 0.5rem;
  }

  .tag {
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    background-color: var(--primary-50);
    color: var(--primary-700);
    padding: 0.5rem 0.9rem;
    font-size: 0.9rem;
    border: 1px solid var(--primary-50);
    border-radius: 8px;

    svg {
      cursor: pointer;
      height: 0.8rem;
      margin-left: 0.7rem;
      stroke: var(--primary-700);
    }
  }
`;

const Search = styled.div`
  width: min(25rem, 100%);
  > div {
    width: 100%;
  }
  position: relative;
`;

const DeleteSearch = styled.div`
  position: absolute;
  stroke: var(--gray-600);
  top: 0;
  bottom: 0;
  right: 1rem;
  width: 15px !important;
  height: 15px;
  margin-top: auto;
  margin-bottom: auto;
  display: grid;
  place-items: center;
  cursor: pointer;
`;

type DropDownListProps = {
  title: string | JSX.Element;
};
const DropDownList: FC<DropDownListProps> = ({ title, children }) => {
  const [open, switchOpen] = useReducer((p) => !p, false);
  const list = useRef<HTMLDivElement>(null);

  return (
    <div>
      <ColumnTitle onClick={switchOpen}>
        {title}
        <div className={open ? 'open' : ''}>
          <ChevronDownIcon />
        </div>
      </ColumnTitle>
      <div
        style={{
          overflow: 'hidden',
          transition: '.2s',
          height: open
            ? list && list.current
              ? list.current.clientHeight
              : 'auto'
            : 0,
        }}
      >
        <CheckboxList ref={list}>{children}</CheckboxList>
      </div>
    </div>
  );
};

type Tag = {
  name: string;
  label: string;
  labelComponent?: React.ReactElement | string;
};
type TagColumn<T extends string | number | symbol> = {
  columnName: T;
  columnTitle: string;
  tags: Tag[];
};

type Props<T extends string, U = unknown> = {
  tagList: TagColumn<T>[] | null;
  state: {
    tags: Record<T, string[]>;
    search: string;
    take: number;
  } & (U extends string
    ? { customTags: Record<U, unknown> }
    : { customTags?: undefined });
  setTags: (tags: Record<T, string[]>) => void;
  setSearch?: (search: string) => void;
  setTake?: (take: number) => void;
} & (U extends string
  ? {
      customTags: (Omit<TagColumn<U>, 'tags'> & {
        component: React.ReactElement;
        /* eslint-disable @typescript-eslint/no-explicit-any */
        getDisplayText: (value: any) => string;
      })[];
      removeCustomTag: (tagName: U) => void;
    }
  : { customTags?: undefined; removeCustomTag?: undefined });

function getFilters<T extends string, U = unknown>(): FC<Props<T, U>> {
  const Filters: FC<Props<T, U>> = ({
    tagList,
    customTags,
    removeCustomTag,
    state,
    setSearch,
    setTags,
    setTake,
  }) => {
    const [drawerOpen, setDrawerOpen] = useState(false);

    const [displayedSearch, setDisplayedSearch] = useState(state.search);
    const [displayedTags, setDisplayedTags] = useState(state.tags);

    const handleSearchChange: React.ChangeEventHandler<HTMLInputElement> = (
      e,
    ) => {
      setDisplayedSearch(e.target.value);
    };

    useEffect(() => {
      if (setSearch && displayedSearch !== state.search) {
        const searchTimeOut = setTimeout(
          () => setSearch(displayedSearch),
          1000,
        );
        return () => clearTimeout(searchTimeOut);
      }
    }, [displayedSearch, state, setSearch]);

    const handleTagChange = (
      e: React.ChangeEvent<HTMLInputElement>,
      columnName: T,
    ) => {
      const tagsSet = new Set(displayedTags[columnName]);
      if (e.target.checked) {
        tagsSet.add(e.target.name);
      } else {
        tagsSet.delete(e.target.name);
      }
      setDisplayedTags({
        ...displayedTags,
        [columnName]: Array.from(tagsSet),
      });
    };

    const removeTag = useCallback((name: string, columnName: T) => {
      const tagsSet = new Set(state.tags[columnName]);
      tagsSet.delete(name);
      setTags({
        ...state.tags,
        [columnName]: Array.from(tagsSet),
      });
      setDisplayedTags({
        ...state.tags,
        [columnName]: Array.from(tagsSet),
      });
    }, [state, setTags]);

    const TagChips = useMemo(() => {
      if (tagList && tagList[0]) {
        return Object.entries(state.tags).map(([key, value]) => {
          if (!isStringArray(value)) {
            return null;
          }
          return (
            <React.Fragment key={key}>
              {value.map((tag) => (
                <div key={`tag-${tag}`} className="tag">
                  {
                    tagList
                      .find((column) => column.columnName === (key as T))
                      ?.tags.find((item) => item.name === tag)?.label
                  }
                  <div
                    onClick={() => {
                      removeTag(tag, key as T);
                    }}
                  >
                    <CrossIcon />
                  </div>
                </div>
              ))}
            </React.Fragment>
          );
        });
      }
      return null;
    }, [tagList, state, removeTag]);

    const CustomTagChips = useMemo(() => {
      if (customTags && customTags.length && state.customTags) {
        return Object.entries(state.customTags).map(([key, value]) => {
          if (!value) return null;

          const customTag = customTags.find(
            (column) => column.columnName === (key as unknown as U),
          );

          return (
            <React.Fragment key={key}>
              <div key={`tag-${key}`} className="tag">
                {customTag?.columnTitle}
                {': '}
                {customTag?.getDisplayText(value)}
                <div
                  onClick={() => {
                    removeCustomTag && removeCustomTag(key as unknown as U);
                  }}
                >
                  <CrossIcon />
                </div>
              </div>
            </React.Fragment>
          );
        });
      }
    }, [customTags, state, removeCustomTag]);

    return (
      <>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            marginBottom: '1.2rem',
          }}
        >
          {tagList && tagList[0] && (
            <ActionDrawer
              open={drawerOpen}
              close={() => setDrawerOpen(false)}
              title="Filtres"
              subtitle="Appliquer des filtres à la recherche"
              cancel={() => {
                setDisplayedTags(state.tags);
              }}
              apply={() => {
                setTags(displayedTags);
              }}
            >
              {tagList.map((column) => (
                <DropDownList
                  key={`column-${column.columnName}`}
                  title={column.columnTitle}
                >
                  {column.tags.map((tag) => (
                    <LabelledCheckbox
                      key={`labelled-checkbox-${tag.name}`}
                      label={tag.labelComponent || tag.label}
                      name={tag.name}
                      checked={displayedTags[column.columnName].includes(
                        tag.name,
                      )}
                      onChange={(e) => handleTagChange(e, column.columnName)}
                    />
                  ))}
                </DropDownList>
              ))}
              {customTags?.map((column) => (
                <DropDownList
                  key={`column-${column.columnName}`}
                  title={column.columnTitle}
                >
                  {column.component}
                </DropDownList>
              ))}
            </ActionDrawer>
          )}
          <Container>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              {!!setSearch && (
                <Search>
                  <Input
                    type="text"
                    placeholder="Rechercher"
                    icon={<SearchIcon />}
                    value={displayedSearch}
                    onChange={handleSearchChange}
                  />
                  {displayedSearch.length > 0 && (
                    <DeleteSearch
                      onClick={() => {
                        setSearch('');
                        setDisplayedSearch('');
                      }}
                    >
                      <CrossIcon />
                    </DeleteSearch>
                  )}
                </Search>
              )}
              {tagList && tagList[0] ? (
                <Button
                  color="primary"
                  icon={<FilterIcon />}
                  onClick={() => setDrawerOpen(true)}
                  style={{ padding: '0.7rem 0.9rem' }}
                >
                  Filtrer
                </Button>
              ) : (
                <div></div>
              )}
            </div>
            {!!setTake && <TakeSelect take={state.take} setTake={setTake} />}
          </Container>
          {TagChips?.some((child) => !!child?.props.children.length) ||
          CustomTagChips?.some((child) => !!child) ? (
            <Tags>
              {TagChips}
              {CustomTagChips}
            </Tags>
          ) : null}
        </div>
      </>
    );
  };
  return Filters;
}
export default getFilters;
